import React, { useContext, useState, useEffect, useCallback } from 'react';
import { MB_accessTokenUtils } from '@mightybyte/rnw.utils.access-token-utils';
import { getRootNavRef } from '../navigators/RootNavigator';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { CurrentUserType } from '../graphql/getCurrentUser';
import { useCheckAndUpdateStreakMutation } from '../graphql/journey/useCheckAndUpdateStreakOnLoadMutation.gql';

export const signedInContextGlobalFunction: {
  signOut?: (params: ISignOut) => void;
  getSignedInStatus?: () => SIGNED_IN_STATUS;
} = {};

export interface ISignOut {
  skipSignOutRequest?: boolean;
  showExpiredError?: boolean;
  makeRefreshTokenCall?: boolean;
}

export const enum SIGNED_IN_STATUS {
  loading = 'loading',
  signedIn = 'signedIn',
  signedOut = 'signedOut',
}

export interface ISignedInContextType {
  isSignedIn: boolean;
  signedInStatus: SIGNED_IN_STATUS;
  setSignedInStatus: (isSignedIn: SIGNED_IN_STATUS) => void;
  user: CurrentUserType | null;
  setUser: (value: CurrentUserType) => void;
  signOut: (params?: ISignOut) => Promise<void>;
  currentStreak: number;
  streakHistory: { streakCount: number; earnedAt: string; seen?: boolean }[];
  refetchStreakHistory: () => Promise<void>;
}

const SignedInContext = React.createContext<ISignedInContextType>({
  isSignedIn: false,
  signedInStatus: SIGNED_IN_STATUS.loading,
  setSignedInStatus: () => {},
  signOut: async () => {},
  user: null,
  setUser: () => {},
  currentStreak: 0,
  streakHistory: [],
  refetchStreakHistory: async () => {},
});

const SignedInStatusProvider = ({ children }: { children: React.ReactNode }) => {
  const [signedInStatus, setSignedInStatusInternal] = useState<SIGNED_IN_STATUS>(
    SIGNED_IN_STATUS.loading,
  );
  const [user, setUser] = useState<CurrentUserType | null>(null);
  const [currentStreak, setCurrentStreak] = useState<number>(0);
  const [streakHistory, setStreakHistory] = useState<
    { streakCount: number; earnedAt: string; seen?: boolean }[]
  >([]);

  const [checkAndUpdateStreakOnLoad] = useCheckAndUpdateStreakMutation();

  const refetchStreakHistory = useCallback(async () => {
    if (signedInStatus === SIGNED_IN_STATUS.signedIn && user?.id) {
      try {
        const { data } = await checkAndUpdateStreakOnLoad({
          variables: { userId: user.id },
        });
        if (data?.checkAndUpdateStreakOnLoad) {
          const streak = data.checkAndUpdateStreakOnLoad;
          setCurrentStreak(streak.currentStreak);
          setStreakHistory(streak.history ?? []);
        }
      } catch (err) {
        console.error('❌ Failed to refetch streak:', err);
      }
    }
  }, [signedInStatus, user, checkAndUpdateStreakOnLoad]);

  // 🔥 Initial streak fetch on sign-in
  useEffect(() => {
    refetchStreakHistory();
  }, [refetchStreakHistory]);

  const signOut = async (params?: ISignOut) => {
    console.log('Params when sign out was called', params);

    if (signedInStatus !== SIGNED_IN_STATUS.signedOut) {
      setSignedInStatusInternal(SIGNED_IN_STATUS.signedOut);
      setUser(null);
      setCurrentStreak(0);
      setStreakHistory([]);
      await MB_accessTokenUtils.removeAccessToken();
      await AsyncStorage.removeItem('playing-name');

      getRootNavRef()?.reset({
        index: 0,
        routes: [{ name: 'Lobby' }],
      });
    }
  };

  const setSignedInStatus = (isSignedIn: SIGNED_IN_STATUS) => {
    setSignedInStatusInternal(isSignedIn);
  };

  signedInContextGlobalFunction.signOut = signOut;
  signedInContextGlobalFunction.getSignedInStatus = () => signedInStatus;

  return (
    <SignedInContext.Provider
      value={{
        isSignedIn: signedInStatus === SIGNED_IN_STATUS.signedIn,
        signedInStatus,
        setSignedInStatus,
        signOut,
        user,
        setUser,
        currentStreak,
        streakHistory,
        refetchStreakHistory,
      }}
    >
      {children}
    </SignedInContext.Provider>
  );
};

function useSignedInContext() {
  const context = useContext(SignedInContext);
  if (context === undefined) {
    throw new Error('useSignedInContext must be used within a SignedInStatusContextProvider');
  }
  return context;
}

export { SignedInStatusProvider, SignedInContext, useSignedInContext };
